import { OptionAdornment, OptionIcon } from '@app/src/components/Form/Select'
import SelectPaper from '@app/src/components/Form/Select/SelectPaper'
import TextField from '@app/src/components/Ui/TextField'
import { capitalize } from '@app/src/utils/helpers'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import type { AutocompleteInputChangeReason, AutocompleteProps, InputProps, TextFieldVariants } from '@mui/material'
import { Autocomplete, Box, Chip, CircularProgress, createFilterOptions, Tooltip, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import type { SetStateAction } from 'react'
import React, { useCallback, useMemo } from 'react'
import type { FieldErrors } from 'react-hook-form'
import { useIntl } from 'react-intl'

export const SELECT_ALL_OPTION_VALUE = 'SELECT_ALL_OPTION_VALUE'

export const useAutocompleteStyles = makeStyles(({ shadows, palette, spacing }) => ({
  clearIndicator: {
    color: palette.primary.main,
  },
  popupIndicator: {
    color: palette.primary.main,
  },
  paper: {
    boxShadow: shadows[10],
    borderRadius: 12,
  },
  icon: {
    color: palette.primary.main,
  },
  tooltip: {
    marginTop: 2,
  },
  chip: {
    maxWidth: 200,
  },
  autoCompleteGroup: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    paddingLeft: spacing(2),
    paddingRight: spacing(2),
  },
}))

export interface Option<T = number | string> {
  label: string
  value: T
  adornment?: string
  //Not used unless renderOption is used
  additionalText?: string
  disabled?: boolean
  tooltip?: string
}

export interface PassedThroughProps<T> {
  fieldLabel: string
  size?: 'medium' | 'small'
  error?: FieldErrors | string //todo typing : check if we ever send a FieldError
  disabled?: boolean
  isLoading?: boolean
  actionButtons?: JSX.Element
  modifiers?: JSX.Element
  enableSelectAll?: boolean
  hoveringLabel?: boolean
  groupBy?: AutocompleteProps<Option<T>, false, undefined, undefined>['groupBy']
  renderOption?: AutocompleteProps<Option<T>, boolean, false, false, 'div'>['renderOption']
  getOptionKey?: AutocompleteProps<Option<T>, boolean, false, false>['getOptionKey']
  disableListWrap?: boolean
  // Lets arrow-key navigation land on disabled options so their reason can be read out. Selection stays
  // blocked - MUI's own Enter handler checks getOptionDisabled.
  disabledItemsFocusable?: boolean
  ListboxComponent?: React.JSXElementConstructor<React.HTMLAttributes<HTMLElement>>
  fullWidth?: boolean
  variant?: TextFieldVariants
  name?: string
  InputProps?: InputProps
  limitTags?: number
  helperText?: string
}

interface CommonProps<T> {
  onChange: (value: Option<T> | Option<T>[] | null) => void
  open: boolean
  setOpen: React.Dispatch<SetStateAction<boolean>>
  loading: boolean
  optionsError?: string | null
  options: Option<T>[]
  required?: boolean
  onClick?: () => void
  onInputChange?: (event: React.ChangeEvent<{}>, value: string, reason: AutocompleteInputChangeReason) => void
}

interface SingleModeProps<T> {
  multiple?: false
  enableSelectAll?: false
  value: Option<T> | null
  isOptionEqualToValue?: AutocompleteProps<Option<T>, boolean, false, false>['isOptionEqualToValue']
  disableClearable?: boolean
}

interface MultipleModeProps<T> {
  multiple: true
  enableSelectAll: boolean
  value: Option<T>[]
  isOptionEqualToValue?: AutocompleteProps<Option<T>, boolean, false, false>['isOptionEqualToValue']
  disableClearable?: false
}

export type SimpleSelectTruncateProps<T> = SingleModeProps<T> | MultipleModeProps<T>

export type ControlledProps<T> = (CommonProps<T> & PassedThroughProps<T>) & SimpleSelectTruncateProps<T>

export type SimpleSelectProps<T = number | string> = ControlledProps<T>

export function isMultipleSelectValue<T>(option: Option<T> | Option<T>[] | null): option is Option<T>[] {
  return Array.isArray(option)
}
export function isSingleSelectValue<T>(option: Option<T> | Option<T>[] | null): option is Option<T> {
  return option !== null && !Array.isArray(option)
}

const valueHasBeenSelected = <T,>(value: Option<T> | Option<T>[] | null) => {
  if (value === null) return false
  if ('length' in value) return value.length > 0
  return Boolean(value.value)
}

function SimpleSelect<T>({
  options,
  size,
  required,
  error,
  fieldLabel,
  disabled,
  isLoading,
  multiple,
  enableSelectAll,
  hoveringLabel,
  actionButtons,
  modifiers,
  groupBy,
  renderOption,
  getOptionKey,
  disableListWrap,
  disabledItemsFocusable,
  ListboxComponent,
  fullWidth,
  onChange,
  value,
  open,
  setOpen,
  loading,
  optionsError,
  onClick,
  onInputChange,
  isOptionEqualToValue,
  variant = 'filled',
  name,
  InputProps,
  limitTags = 1,
  helperText,
  disableClearable,
}: SimpleSelectProps<T>): JSX.Element {
  const classes = useAutocompleteStyles()
  const { formatMessage } = useIntl()

  // Calculate height based on options count and viewport size
  const listBoxHeight = useMemo(() => {
    if (typeof window !== 'undefined') {
      const viewportHeight = window.innerHeight
      const itemHeight = 70
      const maxItemsToShow = Math.min(Math.floor((viewportHeight * 0.4) / itemHeight), 12)
      const actualHeight = Math.min(options.length * itemHeight, maxItemsToShow * itemHeight)
      return `${actualHeight}px`
    }
    return '400px'
  }, [options.length])

  const shouldShowHovering = variant === 'filled' || hoveringLabel

  const allSelected = enableSelectAll && value.length === options.length
  const filter = createFilterOptions<Option<T>>({
    stringify: option => `${option.label}${option.additionalText ?? ''}`,
  })

  const defaultRenderOption: PassedThroughProps<T>['renderOption'] = (props, option, { selected }) => {
    const selectAllProps = option.value === SELECT_ALL_OPTION_VALUE ? { selected: Boolean(allSelected) } : {}

    const content = (
      <>
        <OptionIcon selected={selected} multiple={multiple} {...selectAllProps} />
        {option?.label || ''}
        <OptionAdornment option={option} />
      </>
    )

    const { key: optionKey, ...liProps } = props as unknown as React.ComponentPropsWithoutRef<'li'> & {
      key?: React.Key
    }

    if (option.tooltip) {
      return (
        <li key={optionKey} {...liProps}>
          <Tooltip title={option.tooltip} arrow>
            <span>{content}</span>
          </Tooltip>
        </li>
      )
    }

    return (
      <li key={optionKey} {...liProps}>
        {content}
      </li>
    )
  }

  const handleChange: AutocompleteProps<Option<T>, boolean, false, false>['onChange'] = (_event, newValue, reason) => {
    if ((enableSelectAll && reason === 'selectOption') || reason === 'removeOption') {
      const selectAllOption = [newValue].flat().find(option => option?.value === SELECT_ALL_OPTION_VALUE)

      if (selectAllOption) {
        onChange(allSelected ? [] : options)
      } else {
        onChange(newValue)
      }
    } else if (reason === 'clear' && multiple) {
      // When clearing via clear button, preserve disabled options from current value
      const disabledOptions =
        Array.isArray(value) && value.length > 0 ? value.filter(opt => opt?.disabled === true) : []
      onChange(disabledOptions)
    } else {
      onChange(newValue)
    }
  }

  const PaperComponent: React.JSXElementConstructor<React.HTMLAttributes<HTMLElement>> = useCallback(
    ({ children }) => (
      <SelectPaper actionButtons={actionButtons} modifiers={modifiers}>
        {children}
      </SelectPaper>
    ),
    [actionButtons, modifiers],
  )

  return (
    <Autocomplete
      fullWidth={fullWidth}
      size={size}
      groupBy={groupBy}
      clearOnBlur
      disableClearable={disableClearable as false} //Casts to false, since for multiple === true, only false is accepted, but this component supports both
      disableListWrap={disableListWrap}
      disabledItemsFocusable={disabledItemsFocusable}
      ListboxComponent={ListboxComponent}
      popupIcon={<ExpandMoreIcon />}
      classes={{
        popupIndicator: classes.popupIndicator,
        paper: classes.paper,
      }}
      isOptionEqualToValue={
        isOptionEqualToValue ? isOptionEqualToValue : (option, value) => option.value === value.value
      }
      getOptionKey={getOptionKey}
      multiple={multiple}
      disabled={disabled}
      disableCloseOnSelect={multiple}
      open={open}
      onOpen={(): void => {
        setOpen(true)
      }}
      onClose={(): void => {
        setOpen(false)
      }}
      onFocus={(): void => {
        onClick && onClick()
        setOpen(true)
      }}
      loading={loading}
      noOptionsText={optionsError || formatMessage({ id: 'general.noResults' })}
      getOptionLabel={option =>
        option?.label ? capitalize(option.label) : formatMessage({ id: 'form.unnamedResource' })
      }
      options={options}
      getOptionDisabled={option => Boolean(option.disabled)}
      filterOptions={(options, state) => {
        const filtered = [...filter(options, state)]

        if (enableSelectAll) {
          return [
            { label: formatMessage({ id: 'general.selectAll' }), value: SELECT_ALL_OPTION_VALUE as T },
            ...filter(options, state),
          ]
        }

        return filtered
      }}
      renderInput={(params): JSX.Element => (
        <TextField
          variant={hoveringLabel ? 'filled' : variant}
          label={
            isLoading
              ? formatMessage(
                  { id: 'general.loadingField' },
                  {
                    fieldLabel: fieldLabel,
                  },
                )
              : fieldLabel
          }
          required={required && !valueHasBeenSelected(value)}
          hoveringLabel={shouldShowHovering}
          placeholder={shouldShowHovering ? formatMessage({ id: 'textFieldPlaceholders.select' }) : undefined}
          error={Boolean(error)}
          helperText={error ? (typeof error === 'string' ? error : error?.message) : helperText}
          {...params}
          InputProps={{
            ...params.InputProps,
            ...InputProps,
            name,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
                {InputProps?.endAdornment}
              </>
            ),
          }}
          InputLabelProps={{
            variant: hoveringLabel ? 'filled' : variant,
            required: required,
            ...(params.InputLabelProps ?? {}),
          }}
          translate="no"
        />
      )}
      renderTags={(value, getTagProps) =>
        value?.map((option, index) => {
          if (!option) return null

          const tagProps = getTagProps({ index })
          const chipProps = option.disabled ? { ...tagProps, onDelete: undefined } : tagProps

          return (
            <Tooltip key={index} title={option.label} classes={{ tooltipPlacementBottom: classes.tooltip }} arrow>
              <Chip label={option.label} size="small" {...chipProps} classes={{ root: classes.chip }} />
            </Tooltip>
          )
        })
      }
      renderGroup={(params): JSX.Element => (
        <Box key={params.key}>
          <Typography variant="subtitle1" className={classes.autoCompleteGroup}>
            {params.group}
          </Typography>
          {params.children}
        </Box>
      )}
      onChange={handleChange}
      onInputChange={onInputChange}
      value={value}
      renderOption={renderOption || defaultRenderOption}
      limitTags={limitTags}
      PaperComponent={PaperComponent}
      ListboxProps={{
        style: { maxHeight: listBoxHeight },
      }}
    ></Autocomplete>
  )
}

export default SimpleSelect
